import React from "react";
import { Link } from "react-router-dom";
import {
  Languages,
  Dumbbell,
  Trophy,
  HeartPulse,
  Brain,
  Stethoscope,
  Sprout,
  ArrowRight,
  ShieldCheck,
  Users,
  Calendar,
  Sparkles,
  Info,
  Globe2,
  MapPin,
  Clock,
} from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { IMAGES } from "@/lib/site";

// ─── OPTIONS (4) ────────────────────────────────────────────────
const OPTIONS = [
  {
    key: "lvb",
    icon: Languages,
    ancre: "lvb",
    couleur: "bg-brand-slate text-brand-cream",
    tag: "Langues vivantes",
    titre: "LVB — Espagnol ou Allemand",
  },
  {
    key: "uf3s",
    icon: Dumbbell,
    ancre: "uf3s",
    couleur: "bg-brand-sand-dark text-white",
    tag: "Sport",
    titre: "UF3S — Secteur Sportif",
  },
  {
    key: "shn",
    icon: Trophy,
    ancre: "shn",
    couleur: "bg-brand-slate-dark text-brand-cream",
    tag: "Haut niveau",
    titre: "Sportifs de Haut Niveau",
  },
  {
    key: "erasmus",
    icon: Globe2,
    ancre: "erasmus",
    couleur: "bg-brand-sage-dark text-white",
    tag: "Mobilité européenne",
    titre: "Erasmus+",
  },
];

// ─── DISPOSITIFS (2) ────────────────────────────────────────────
const DISPOSITIFS = [
  {
    key: "sante",
    icon: HeartPulse,
    ancre: "sante",
    couleur: "bg-brand-sand-dark text-white",
    tag: "Bien-être",
    titre: "Pôle Santé & Bien-être",
  },
  {
    key: "as",
    icon: Trophy,
    ancre: "as",
    couleur: "bg-brand-slate text-brand-cream",
    tag: "Association sportive",
    titre: "Association Sportive",
  },
];

const POLE_SANTE = [
  {
    icon: Brain,
    titre: "Psychologue",
    texte: "Un espace d'écoute pour aborder les difficultés personnelles, les questionnements d'orientation ou les moments de fragilité.",
  },
  {
    icon: Stethoscope,
    titre: "Infirmière scolaire",
    texte: "Présente chaque semaine pour la santé des élèves : soins, prévention, éducation à la santé et lien avec les familles.",
  },
  {
    icon: Sprout,
    titre: "Coach de vie",
    texte: "Un accompagnement personnalisé pour développer la confiance en soi, l'autonomie et la motivation dans les études.",
  },
];

const SPORTS_AS = [
  {
    key: "futsal",
    nom: "Futsal",
    emoji: "⚽",
    image: "https://images.unsplash.com/photo-1517927033932-b3d18e61fb3a?auto=format&fit=crop&w=1400&q=80",
    horaire: "Mercredi · 13h30 – 15h30",
    lieu: "Gymnase du lycée",
    description: "Une variante du football en salle : plus rapide, plus technique. Un jeu collectif intense qui développe la coordination, la vision de jeu et le sens tactique. Ouvert à tous les niveaux, débutants bienvenus.",
    tags: ["Débutants OK", "Mixte", "Compétitions UNSS"],
  },
  {
    key: "basketball",
    nom: "Basketball",
    emoji: "🏀",
    image: "https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=1400&q=80",
    horaire: "Jeudi · 12h30 – 14h00",
    lieu: "Gymnase du lycée",
    description: "Un sport collectif alliant vitesse, agilité et esprit d'équipe. Idéal pour développer sa condition physique tout en s'amusant avec les copains. Encadré par un enseignant d'EPS diplômé.",
    tags: ["Tous niveaux", "Mixte", "Tournois inter-lycées"],
  },
];

export default function Dispositifs() {
  return (
    <div data-testid="dispositifs-page">
      <PageHero
        eyebrow="Options & dispositifs d'accompagnement"
        title="Des atouts uniques pour révéler les talents de chaque élève"
        subtitle="Au lycée Joseph Wresinski, chaque jeune peut choisir parmi 4 options (langues, sport, mobilité européenne) et bénéficier de 2 dispositifs d'accompagnement (santé & bien-être, association sportive) pour s'épanouir pleinement."
        image={IMAGES.classroom}
      />

      {/* Sommaire — 4 options */}
      <Section eyebrow="4 options au choix" title="Enrichir son parcours">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {OPTIONS.map((d) => (
            <a
              key={d.key}
              href={d.key === "erasmus" ? "/erasmus" : `#${d.ancre}`}
              data-testid={`option-nav-${d.key}`}
              className="group flex flex-col rounded-3xl border border-black/5 bg-brand-cream p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <span className={`grid h-12 w-12 place-items-center rounded-2xl ${d.couleur}`}>
                <d.icon className="h-5 w-5" />
              </span>
              <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-brand-sand-dark">
                {d.tag}
              </p>
              <p className="mt-2 font-heading text-base font-bold leading-tight text-brand-slate-dark">
                {d.titre}
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-slate transition-colors group-hover:text-brand-sand-dark">
                Découvrir
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </a>
          ))}
        </div>
      </Section>

      {/* Sommaire — 2 dispositifs */}
      <Section wrapper="bg-brand-sand-wrapper/40" eyebrow="2 dispositifs d'accompagnement" title="S'épanouir au quotidien">
        <div className="grid gap-6 md:grid-cols-2">
          {DISPOSITIFS.map((d) => (
            <a
              key={d.key}
              href={`#${d.ancre}`}
              data-testid={`dispositif-nav-${d.key}`}
              className="group flex flex-col rounded-3xl border border-black/5 bg-brand-cream p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <span className={`grid h-12 w-12 place-items-center rounded-2xl ${d.couleur}`}>
                <d.icon className="h-5 w-5" />
              </span>
              <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-brand-sand-dark">
                {d.tag}
              </p>
              <p className="mt-2 font-heading text-xl font-bold leading-tight text-brand-slate-dark">
                {d.titre}
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-slate transition-colors group-hover:text-brand-sand-dark">
                Découvrir
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </a>
          ))}
        </div>
      </Section>

      {/* ─── LVB ─── */}
      <Section id="lvb" eyebrow="Option 1 · Langues vivantes" title="LVB — Espagnol ou Allemand 🌍">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-start">
          <div className="space-y-5">
            <p className="text-base leading-relaxed text-muted-foreground">
              Tu souhaites continuer à perfectionner ton espagnol ou ton allemand ? Choisir une seconde
              langue vivante, c'est l'occasion de développer tes compétences linguistiques, de
              découvrir d'autres cultures et d'élargir tes opportunités d'études et professionnelles.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { icon: Sparkles, t: "S'ouvrir à d'autres cultures", d: "Découvrir la civilisation hispanophone ou germanophone." },
                { icon: Users, t: "Renforcer son CV", d: "Une compétence linguistique très valorisée par les recruteurs." },
                { icon: Calendar, t: "Faciliter les échanges", d: "Un vrai plus pour un projet Erasmus+ ou un stage à l'étranger." },
                { icon: ShieldCheck, t: "Aller plus loin", d: "Préparer son futur, du BTS aux écoles post-bac." },
              ].map((c) => (
                <div
                  key={c.t}
                  className="rounded-2xl border border-black/5 bg-brand-cream p-5 transition-all hover:-translate-y-0.5 hover:shadow-md"
                >
                  <c.icon className="h-6 w-6 text-brand-slate" />
                  <p className="mt-3 font-heading text-sm font-bold text-brand-slate-dark">{c.t}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{c.d}</p>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-3 rounded-2xl border border-brand-sand/40 bg-brand-cream p-4 text-sm">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand-sand-dark" />
              <p className="text-muted-foreground">
                <strong className="text-brand-slate-dark">Disponibilité selon les filières.</strong>{" "}
                Renseigne-toi auprès du secrétariat lors de ton inscription.
              </p>
            </div>
          </div>
          <Card className="bg-brand-slate text-brand-cream">
            <div className="flex gap-2">
              <span className="rounded-full bg-brand-sand/20 px-3 py-1 text-xs font-semibold text-brand-sand">Espagnol</span>
              <span className="rounded-full bg-brand-sand/20 px-3 py-1 text-xs font-semibold text-brand-sand">Allemand</span>
            </div>
            <h3 className="mt-5 font-heading text-2xl font-bold">Deux langues au choix</h3>
            <p className="mt-3 text-sm leading-relaxed text-brand-cream/85">
              Des cours dispensés par des enseignants qualifiés, avec une approche à l'oral et des
              projets ludiques pour maintenir la motivation.
            </p>
            <Link
              to="/contact"
              data-testid="lvb-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-sand-dark px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
            >
              En savoir plus
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Card>
        </div>
      </Section>

      {/* ─── UF3S ─── */}
      <Section id="uf3s" wrapper="bg-brand-sand-wrapper/40" eyebrow="Option 2 · Sport" title="Option UF3S — Sensibilisation au Secteur Sportif 🏃">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-start">
          <div className="overflow-hidden rounded-3xl bg-brand-sand-wrapper/60 p-6">
            <div className="overflow-hidden rounded-2xl">
              <img
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=1000&q=80"
                alt="Élèves en séance sportive"
                className="h-52 w-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
            <Dumbbell className="mt-6 h-8 w-8 text-brand-sand-dark" />
            <h3 className="mt-4 font-heading text-2xl font-bold text-brand-slate-dark">
              Un parcours sportif au cœur de ta formation
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              L'UF3S est une <strong className="text-brand-slate-dark">Unité Facultative</strong> qui
              te permet de découvrir les métiers du sport tout en pratiquant régulièrement.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["MA", "MCV", "AGORA", "AEPA", "ASSP"].map((f) => (
                <span key={f} className="rounded-full bg-brand-cream px-3 py-1 text-xs font-semibold text-brand-slate-dark">
                  Bac Pro {f}
                </span>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Disponible selon les filières MA, MCV, AGORA, AEPA et ASSP.
            </p>
          </div>

          <div className="space-y-5">
            <p className="text-base leading-relaxed text-muted-foreground">
              Tu es passionné(e) de sport et tu souhaites en faire un atout pour ton avenir ? L'option
              UF3S te permet de pratiquer une activité sportive au sein du lycée tout en développant
              des compétences dans l'animation, l'encadrement et la conduite de projets sportifs.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { t: "Pratique régulière", d: "Séances hebdomadaires encadrées par des enseignants d'EPS spécialisés." },
                { t: "Animation & encadrement", d: "Apprendre à guider un groupe, adapter les activités selon les publics." },
                { t: "Projet sportif", d: "Concevoir un événement, une compétition, une action de sensibilisation." },
                { t: "Passerelle métiers", d: "Un pré-requis idéal pour un BPJEPS ou une carrière dans l'animation." },
              ].map((c) => (
                <div key={c.t} className="rounded-2xl border border-black/5 bg-brand-cream p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
                  <p className="font-heading text-sm font-bold text-brand-slate-dark">{c.t}</p>
                  <p className="mt-2 text-xs text-muted-foreground">{c.d}</p>
                </div>
              ))}
            </div>
            <Link
              to="/contact"
              data-testid="uf3s-cta"
              className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-5 py-2.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
            >
              En savoir plus
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* ─── SHN ─── */}
      <Section id="shn" wrapper="bg-brand-slate-dark text-brand-cream" eyebrow="Option 3 · Haut niveau" title="Sportifs de Haut Niveau ⚽">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-base leading-relaxed text-brand-cream/85">
              Le lycée accompagne les élèves sportifs de haut niveau grâce à des{" "}
              <strong className="text-brand-cream">aménagements d'emploi du temps</strong> leur
              permettant de concilier réussite scolaire et pratique sportive intensive.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-brand-cream/90">
              {[
                "Emploi du temps aménagé selon les entraînements et compétitions",
                "Rattrapage personnalisé après une compétition ou un déplacement",
                "Tutorat pédagogique par un référent SHN",
                "Suivi individualisé de la charge scolaire et sportive",
              ].map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <Trophy className="mt-0.5 h-4 w-4 shrink-0 text-brand-sand" />
                  {it}
                </li>
              ))}
            </ul>
            <p className="mt-8 text-sm font-semibold uppercase tracking-[0.18em] text-brand-sand">
              Nos partenaires clubs professionnels
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="rounded-3xl border border-white/10 bg-white/5 p-5 transition-all hover:-translate-y-0.5 hover:bg-white/10">
                <div className="flex items-center gap-3">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-sand text-brand-slate-dark font-heading text-lg font-bold">SCO</span>
                  <div>
                    <p className="font-heading text-base font-bold text-brand-cream">Angers SCO</p>
                    <p className="text-xs text-brand-cream/70">Football · Ligue 1</p>
                  </div>
                </div>
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/5 p-5 transition-all hover:-translate-y-0.5 hover:bg-white/10">
                <div className="flex items-center gap-3">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-sand text-brand-slate-dark font-heading text-lg font-bold">DA</span>
                  <div>
                    <p className="font-heading text-base font-bold text-brand-cream">Ducs d'Angers</p>
                    <p className="text-xs text-brand-cream/70">Hockey · Ligue Magnus</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl">
            <img
              src="https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1200&q=80"
              alt="Sportif de haut niveau en entraînement"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </Section>

      {/* ─── ERASMUS+ (résumé + lien vers page dédiée) ─── */}
      <Section id="erasmus" eyebrow="Option 4 · Mobilité européenne" title="Erasmus+ 🌍">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div className="space-y-5">
            <p className="text-base leading-relaxed text-muted-foreground">
              Labellisé Erasmus+, notre lycée offre à ses élèves l'opportunité de réaliser{" "}
              <strong className="text-brand-slate-dark">4 semaines de formation à l'étranger</strong>{" "}
              (Irlande, Espagne et autres pays européens). Une expérience humaine et professionnelle
              unique, valorisée par le diplôme Europass Mobilité et des points bonus au baccalauréat.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                "4 semaines à l'étranger",
                "Bourse Erasmus+ (participation famille ~600 €)",
                "Europass Mobilité",
                "Points bonus au bac (option mobilité)",
              ].map((it) => (
                <div key={it} className="flex items-start gap-2 rounded-2xl bg-brand-sand-wrapper/50 p-3 text-sm">
                  <Globe2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-sand-dark" />
                  <span className="text-brand-slate-dark">{it}</span>
                </div>
              ))}
            </div>
            <Link
              to="/erasmus"
              data-testid="erasmus-cta"
              className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-5 py-3 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
            >
              Découvrir la page Erasmus+ complète
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="overflow-hidden rounded-3xl">
            <img
              src="https://images.unsplash.com/photo-1503917988258-f87a78e3c995?auto=format&fit=crop&w=1200&q=80"
              alt="Élèves en mobilité européenne"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </Section>

      {/* ─── DISPOSITIF 1 · Pôle Santé ─── */}
      <Section id="sante" wrapper="bg-brand-sand-wrapper/40" eyebrow="Dispositif 1 · Pôle Santé & Bien-être ❤️" title="Un accompagnement humain pour la réussite de chacun">
        <p className="mb-10 max-w-3xl text-base leading-relaxed text-muted-foreground">
          Parce que le bien-être est essentiel à la réussite scolaire, le lycée met à disposition des
          élèves une équipe de professionnels présents chaque semaine au sein de l'établissement.
        </p>
        <div className="grid gap-6 md:grid-cols-3">
          {POLE_SANTE.map((p, i) => (
            <Card key={p.titre} data-testid={`sante-${i}`} className="text-center">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-brand-cream text-brand-sand-dark">
                <p.icon className="h-7 w-7" />
              </div>
              <h3 className="mt-6 font-heading text-xl font-bold text-brand-slate-dark">{p.titre}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.texte}</p>
            </Card>
          ))}
        </div>
        <div className="mt-10 rounded-3xl border border-brand-sage/30 bg-brand-sage/10 p-6 lg:p-8">
          <div className="flex items-start gap-4">
            <ShieldCheck className="mt-1 h-6 w-6 shrink-0 text-brand-sage-dark" />
            <div>
              <p className="font-heading text-base font-bold text-brand-slate-dark">Un cadre confidentiel et bienveillant</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Les élèves peuvent être écoutés, accompagnés et conseillés en toute confidentialité.
                Aucun sujet n'est tabou : difficultés scolaires, mal-être, orientation, relations
                familiales… nos professionnels sont là pour aider chacun à trouver ses propres réponses.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* ─── DISPOSITIF 2 · Association Sportive ─── */}
      <Section id="as" eyebrow="Dispositif 2 · Association Sportive 🏆" title="Rejoins l'AS du lycée : futsal & basketball">
        <p className="mb-10 max-w-3xl text-base leading-relaxed text-muted-foreground">
          Tous les élèves du lycée ont la possibilité d'intégrer l'
          <strong className="text-brand-slate-dark">Association Sportive</strong>, encadrée par nos
          enseignants d'EPS. Deux disciplines sont proposées : le futsal et le basketball. Un moment
          convivial hebdomadaire, ouvert à toutes et tous, du débutant au confirmé.
        </p>

        <div className="grid gap-8 md:grid-cols-2">
          {SPORTS_AS.map((s) => (
            <div
              key={s.key}
              data-testid={`as-sport-${s.key}`}
              className="group overflow-hidden rounded-3xl border border-black/5 bg-brand-cream transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.nom}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-slate-dark/70 via-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-3">
                  <span className="text-4xl">{s.emoji}</span>
                  <span className="font-heading text-3xl font-bold text-brand-cream drop-shadow-lg">
                    {s.nom}
                  </span>
                </div>
              </div>
              <div className="p-7">
                <div className="flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full bg-brand-sand-wrapper px-3 py-1 text-xs font-semibold text-brand-slate-dark"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{s.description}</p>
                <div className="mt-5 flex flex-col gap-2 text-sm text-brand-slate-dark sm:flex-row sm:gap-6">
                  <span className="inline-flex items-center gap-2">
                    <Clock className="h-4 w-4 text-brand-sand-dark" />
                    {s.horaire}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-brand-sand-dark" />
                    {s.lieu}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { icon: Users, t: "Ouverte à tous", d: "Aucun niveau requis, l'esprit d'équipe est la seule condition." },
            { icon: Trophy, t: "Compétitions UNSS", d: "Participation possible aux rencontres inter-lycées de l'UNSS." },
            { icon: Sparkles, t: "Convivialité", d: "Un moment de détente et de partage entre élèves de toutes les classes." },
          ].map((c) => (
            <Card key={c.t}>
              <c.icon className="h-7 w-7 text-brand-sage-dark" />
              <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">{c.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
            </Card>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/contact"
            data-testid="as-cta-inscription"
            className="inline-flex items-center gap-2 rounded-full bg-brand-sand-dark px-5 py-3 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
          >
            S'inscrire à l'AS
            <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="mailto:viescolaire@lpjw.fr"
            data-testid="as-cta-info"
            className="inline-flex items-center gap-2 rounded-full border border-brand-slate/20 px-5 py-3 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
          >
            Contacter les enseignants d'EPS
          </a>
        </div>
      </Section>

      {/* CTA final */}
      <Section wrapper="bg-brand-sand-wrapper/50">
        <div className="rounded-3xl bg-brand-cream p-10 text-center lg:p-16">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-brand-slate-dark sm:text-4xl">
            Une option ou un dispositif t'intéresse ?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
            Contacte-nous pour en savoir plus ou pour évoquer ta situation particulière. Notre équipe
            se tient à ta disposition et à celle de tes parents.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              data-testid="dispositifs-cta-contact"
              className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
            >
              Nous contacter
            </Link>
            <Link
              to="/formations"
              data-testid="dispositifs-cta-formations"
              className="inline-flex items-center gap-2 rounded-full border border-brand-slate/20 px-6 py-3.5 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
            >
              Voir les formations
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
